<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink } from 'vue-router';

import { useError } from '@/shared/composables/useError';

const { error } = useError();

/**
 * Maps our error state onto Nuxt UI's UError props:
 *   error.title       → statusMessage (the heading)
 *   error.description → message (the subtext)
 */
const errorProps = computed(() => {
  if (!error.value) {
    return null;
  }

  return {
    statusCode: error.value.statusCode,
    statusMessage: error.value.title,
    message: error.value.description,
  };
});

const isCritical = computed(() => error.value?.critical === true);

/*
  One illustrative icon per status, in a tinted disc. The tint follows the
  colour roles: «not here» is a calm primary, «not allowed» a warning, only a
  broken server is red. Static class strings — Tailwind cannot see a class
  assembled at runtime.
*/
const STATUS_VISUAL = {
  401: { icon: 'i-lucide-log-in', disc: 'bg-primary/10 ring-primary/5', text: 'text-primary' },
  403: { icon: 'i-lucide-lock', disc: 'bg-warning/10 ring-warning/5', text: 'text-warning' },
  404: { icon: 'i-lucide-compass', disc: 'bg-primary/10 ring-primary/5', text: 'text-primary' },
  error: { icon: 'i-lucide-server-crash', disc: 'bg-error/10 ring-error/5', text: 'text-error' },
} as const;

const visual = computed(() => {
  const code = error.value?.statusCode;

  return code === 401 || code === 403 || code === 404 ? STATUS_VISUAL[code] : STATUS_VISUAL.error;
});

const handleReload = () => {
  window.location.reload();
};
</script>

<template>
  <!--
    Critical replaces the whole shell, so it owns the viewport; otherwise it
    sits in the shell's content column under the mobile top bar.
  -->
  <UError
    v-if="errorProps"
    :error="errorProps"
    :clear="false"
    data-testid="u-error"
    :ui="{
      root: ['w-full px-4 py-16', isCritical ? 'min-h-svh' : 'min-h-[80svh]'],
      statusCode: 'flex flex-col items-center gap-5 text-sm font-semibold text-muted tabular-nums',
      statusMessage: 'mt-2 text-2xl font-bold sm:text-3xl',
      message: 'mt-2 max-w-sm text-base leading-relaxed text-toned',
      links: 'mt-8',
    }"
  >
    <template #statusCode>
      <span
        class="flex size-20 items-center justify-center rounded-full ring-8"
        :class="visual.disc"
      >
        <UIcon
          :name="visual.icon"
          class="size-9"
          :class="visual.text"
        />
      </span>
      <span>Ошибка {{ errorProps.statusCode }}</span>
    </template>

    <template #links>
      <!-- Critical: the app cannot continue, so the only way out is a reload. -->
      <UButton
        v-if="isCritical"
        size="lg"
        color="primary"
        icon="i-lucide-refresh-cw"
        label="Перезагрузить страницу"
        @click="handleReload"
      />
      <!-- Non-critical: the error middleware clears it when navigation starts. -->
      <UButton
        v-else
        :as="RouterLink"
        to="/"
        size="lg"
        color="primary"
        icon="i-lucide-house"
        label="На главную"
      />
    </template>
  </UError>
</template>
